<template>
  <div v-if="_contract && _contract.rentalObject">
    <RentalObjectTemplate :rentalObject="_contract.rentalObject" :imageUrl="imageUrl">
      <template v-slot="{ subCaption, area, objectType, blueprint, toggle, expanded }">
        <div class="p-4 md:p-6">
          <h3 class="text-t6 mb-4">Avtalsinformation</h3>

          <div class="text-base grid-cols-2 gap-x-8 gap-y-4 md:grid">
            <div class="flex">
              <p class="font-semibold">Objektnummer:</p>
              <p class="ml-2">{{ _contract.rentalObjectCode }}</p>
            </div>
            <div class="flex">
              <p class="font-semibold">Hyra:</p>
              <p class="ml-2" v-if="_contract.baseRent">
                {{ addSpace(Math.round(_contract.baseRent.toString().replace(/,/g, '.'))) }} kr
              </p>
            </div>
            <div class="flex" v-if="_contract.dwellingNumber">
              <p class="font-semibold">Lägenhetsnummer:</p>
              <p class="ml-2">{{ _contract.dwellingNumber }}</p>
            </div>
            <div class="flex">
              <p class="font-semibold">Kontraktet gäller:</p>

              <p class="ml-2">
                {{ formatDate(_contract.fromDate, 'YYYY-MM-DD') }} -
                <span
                  v-if="
                    (_contract.status == 'preliminarilyTerminated' ||
                      _contract.status == 'terminated') &&
                    _contract.validContractPeriodTo != null
                  "
                >
                  {{ formatDate(_contract.validContractPeriodTo, 'YYYY-MM-DD') }}
                </span>
                <span
                v-else-if="
                    (_contract.status == 'preliminarilyTerminated' ||
                      _contract.status == 'terminated') &&
                    _contract.validContractPeriodTo == null
                  "
              >

                </span>

                <span v-else>Tillsvidare</span>
              </p>
            </div>

            <div class="flex" v-if="_contract.rentalObjectType != 'parking'">
              <p class="font-semibold">Yta och rum:</p>
              <p class="ml-2">
                {{ _contract.rentalObject.quantityBRA }} m&sup2,
                {{ _contract.rentalObject.subCaption }}
              </p>
            </div>
            <div class="flex">
              <p class="font-semibold">Uppsägningstid:</p>
              <p class="ml-2">{{ cancelPolicy }}</p>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col justify-end gap-2 border-t border-solid border-gray-light px-4 py-4 md:flex-row md:px-6"
        >
          <Button v-if="imageUrl" variant="text" size="large" @click="showDrawing = !showDrawing">
            {{ showDrawing ? 'Dölj planlösning' : 'Visa planlösning' }}
            <template #icon-after>
              <IconFloorPlan />
            </template>
          </Button>

          <Button
            v-if="webapp_config.show_inspectationreports_link && !contractIsSecondary && isResidentalContract"
            variant="secondary"
            size="large"
          >
            <router-link
              class="btn btn-primary btn-padded btn--nomargin"
              :to="{
                name: 'Proxy',
                query: { url: '/XpandWeb/Internet/lb/showdwellinginspection.aspx' },
              }"
            >
              Se besiktningsprotokoll
            </router-link>
            <template #icon-after>
              <IconArrowRight />
            </template>
          </Button>

          <Button
            v-if="showContractTermination"
            variant="danger"
            size="large"
            @click="showTermination = true"
          >
            Säg upp hyresavtal
          </Button>
        </div>

        <div class="p-6" v-if="showDrawing">
          <p v-html="webapp_config.msg.infotext_before_blueprint_contract"></p>
          <img v-if="showDrawing" :src="imageUrl" />
        </div>
        <ContractTermination
          v-if="showTermination"
          :TerminateContractModal="showTermination"
          :contractNumber="_contract.contractNumber"
          :typeOfContract="objectType"
          :hasParking="hasApartmentAndParking"
          @close="showTermination = false"
          @emailSent="terminationConfirmed = true"
        />
      </template>
    </RentalObjectTemplate>
  </div>
</template>

<script>
import ContractMixin from '@/mixins/contract';
import GeneralMixin from '@/mixins/general';
import RentalObjectTemplate from '@/templates/rentalobjecttemplate';
import Modal from '@/components/ui/modal';
import Loader from '@/components/ui/loader';
import { mapGetters } from 'vuex';
import ContractTermination from '@/components/profile/contracttermination';
import { RENTALOBJECT_TYPE_CODES, CONTRACT_STATUSES } from '@/config/constants';
import Button from '@/components/ui/button';

import IconPlus from '@/components/icons/IconPlus';
import IconEdit from '@/components/icons/IconEdit';
import IconTrashCan from '@/components/icons/IconTrashCan';
import IconBullet from '@/components/icons/IconBullet';
import IconArrowRight from '@/components/icons/IconArrowRight';
import IconFloorPlan from '@/components/icons/IconFloorPlan';

export default {
  name: 'ContractListItem',
  mixins: [ContractMixin, GeneralMixin],
  components: {
    RentalObjectTemplate,
    Modal,
    Loader,
    ContractTermination,
    Button,
    IconPlus,
    IconEdit,
    IconTrashCan,
    IconBullet,
    IconArrowRight,
    IconFloorPlan,
  },
  props: {
    contract: {
      type: Object,
      required: false,
    },
    contractNumber: {
      type: String,
      required: false,
    },
    imageUrl: {
      type: String,
      required: false,
    }
  },
  data() {
    return {
      showLoader: true,
      showDrawing: false,
      showTermination: false,
      terminationConfirmed: false,
      CONTRACT_STATUSES,
    };
  },
  computed: {
    ...mapGetters({
      webapp_config: 'webapp_config',
      hasActiveResidentalContract: 'hasActiveResidentalContract',
      hasActiveOrTerminatedResidentalContract: 'hasActiveOrTerminatedResidentalContract'
    }),

    isResidentalContract() {
      return this.contract.rentalObjectType == "apartment";
    },

    contractIsSecondary(){
      return this.contract.isSecondaryTenant;
    },

    isSecondaryTenant() {
      return this.$store.getters.claims.some((el) => el == 'secondaryRentalContract');
    },

    _contract() {
      if (this.contract) {
        return this.contract;
      } else {
        return this.$store.getters.getContractByContractNumber(this.contractNumber);
      }
    },

    /**
     * Kontrollera om det finns kontrakt för både lägenhet och parkering.
     */
    hasApartmentAndParking() {
      let hasApartment = this.$store.getters.hasApartment;
      let hasParking = this.$store.getters.hasParking;

      return hasApartment && hasParking;
    },
    /**
     * Kontrollera om det är en lägenhet
     */
    isApartment() {
      let isApartment = false;
      for (let contract of this.$store.getters.contracts) {
        if (contract.rentalObjectType == 'apartment') {
          isApartment = true;
        }
      }
      return isApartment;
    },
    isParking() {
      let isParking = false;
      for (let contract of this.$store.getters.contracts) {
        if (contract.rentalObjectType == 'parking') {
          isParking = true;
        }
      }
      return isParking;
    },

    showContractTermination() {
      // Funktionnen måste vara aktiverad
      if (
        !this.$store.getters.webapp_config.allow_contract_termination ||
        this._contract.isSecondaryTenant
      )
        return false;

      if (!this._contract) return false;

      let isPerson = this.$store.getters.claims.some((claim) => claim == 'person');
      let isRightKindOfType = ![
        RENTALOBJECT_TYPE_CODES.LOKAL_TYP_1,
        RENTALOBJECT_TYPE_CODES.LOKAL_TYP_2,
        RENTALOBJECT_TYPE_CODES.LOKAL_TYP_4,
        RENTALOBJECT_TYPE_CODES.OVRIGT_TYP_1,
      ].some((code) => code == this._contract.rentalObject.rentalObjectTypeCode);
      let isValid = this._contract.status == CONTRACT_STATUSES.VALID;

      let isSecondaryTenant = !!this._contract.isSecondaryTenant;

      return !isSecondaryTenant && isPerson && isRightKindOfType && isValid;
    },
  },

  created() {
    if (this.contractNumber) {
      return this.$store.cache
        .dispatch('fetchContractByContractNumber', this.contractNumber)
        .finally(() => {
          this.showLoader = false;
        });
    } else if (this.contract) {
      this.showLoader = false;
    } else {
      this.showLoader = false;
    }
  },
};
</script>
